import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, EventEmitter, Output } from '@angular/core';
import { AbstractControlOptions, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { DateAdapter, MAT_DATE_LOCALE, MatNativeDateModule, NativeDateAdapter } from '@angular/material/core';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormFieldEvent } from '@models/forms.interface';
import { DateValidator } from 'src/app/reports/core';
import { BehaviorSubject } from 'rxjs';
import { months } from '@constants/index';

export class CustomDateAdapter extends NativeDateAdapter {
  
  private _isDateAllowed = new BehaviorSubject<boolean>(false);

  public set isDateAllowed(value: boolean) {
    this._isDateAllowed.next(value);
  }

  override format(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.getMonth();
    const yyyy = date.getFullYear();
    const mm = (month + 1).toString().padStart(2, '0');

    return this._isDateAllowed.value
      ? `${day}-${months[month]}-${yyyy}`
      : `${mm}/${yyyy}`;
  }
}

@Component({
  selector: 'app-date-range-field',
  standalone: true,
  imports: [
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatNativeDateModule,
    CommonModule,
    ReactiveFormsModule,
  ],
  providers: [
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE]
    },
  ],
  templateUrl: './date-range-field.component.html',
  styleUrl: './date-range-field.component.scss',
})
export class DateRangeFieldComponent implements OnInit {
  @Input() name = '';
  @Input() label = '';
  @Input() value = '';
  @Input() isRequired = false;
  @Input() isDateAllowed = false;
  @Input() customErrorMessage = ''
  @Output() dateRangeFieldEvent = new EventEmitter<FormFieldEvent>();

  public dateRangeField!: FormGroup;
  public currentYear: number = new Date().getFullYear();
  public maxOneYearFromNow: Date = new Date(new Date().setFullYear(this.currentYear + 1));
  public earliestQueriedReportDate: Date = new Date('June 01, 1991 00:00:00')

  constructor(private dateAdapter: DateAdapter<Date>) {}

  ngOnInit(): void {
    this.buildForm();
    (this.dateAdapter as CustomDateAdapter).isDateAllowed = this.isDateAllowed;
  }

  public buildForm(): void {
    const validators: ValidatorFn[]= [];

    if (this.isRequired) {
      validators.push(Validators.required)
    }
    
    this.dateRangeField = new FormGroup({
      start: new FormControl<Date | string>('', 
      {
        validators,
        nonNullable: true
      }
    ),
      end: new FormControl<Date | string>('', 
      {
        validators,
        nonNullable: true
      }
    )
    },{
      validators: [
       DateValidator.range
      ],
      updateOn: 'change',
    } as AbstractControlOptions)

    this.dateRangeFieldEvent.emit({
      value: '',
      name: this.name
    });
  }

  public setMonthAndYear(fullDate: Date, datePicker: MatDatepicker<unknown>): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if(this.isDateAllowed) return   
 
    const unix: any = {
      input: Date.parse(fullDate.toString()),
    };
    const date = {
      start: this.getDate('start'),
      end: this.getDate('end')
    };

    if (!date.start && !date.end) {
      const update = {
        start: fullDate,
        end: this.getDate('end')
      };

      this.setDate('start', update.start);
      this.dateRangeFieldEvent.emit({
        name: this.name,
        value: update,
        errors: this.dateRangeField?.errors
      });

      datePicker.close();

      return;
    }

    if (date.start && !date.end) {
      unix.start = Date.parse(date.start);
      unix.min = Math.min(unix.start, unix.input);
      unix.max = Math.max(unix.start, unix.input);

      const update = {
        start: new Date(unix.min),
        end: new Date(unix.max),
      }

      this.setDate('start', update.start);
      this.setDate('end', update.end);

      this.dateRangeFieldEvent.emit({
        name: this.name,
        value: update,
        errors: this.dateRangeField?.errors
      })

      datePicker.close();

      return;
    }

    if (!date.start && date.end) {
      unix.end = Date.parse(date.end);
      unix.min = Math.min(unix.end, unix.input);
      unix.max = Math.max(unix.end, unix.input);

      const update = {
        start: new Date(unix.min),
        end: new Date(unix.max),
      }

      this.setDate('start', update.start);
      this.setDate('end', update.end);

      this.dateRangeFieldEvent.emit({
        name: this.name,
        value: update,
        errors: this.dateRangeField?.errors
      })

      datePicker.close();

      return;
    }

    unix.start = Date.parse(date.start);
    unix.end = Date.parse(date.end);

    if (unix.input <= unix.start && unix.input < unix.end) {
      unix.start = unix.input;
      const update = {
        start: new Date(unix.start),
        end: this.getDate('end')
      }

      this.setDate('start', update.start);

      this.dateRangeFieldEvent.emit({
        name: this.name,
        value: update,
        errors: this.dateRangeField?.errors
      })

      datePicker.close();

      return;
    }

    if (unix.input > unix.start && unix.input >= unix.end) {
      unix.end = unix.input;
      const update = {
        start: this.getDate('start'),
        end: new Date(unix.end)
      }

      this.setDate('end', update.end);

      this.dateRangeFieldEvent.emit({
        name: this.name,
        value: update,
        errors: this.dateRangeField?.errors
      })

      datePicker.close();

      return;
    }

    if (unix.input > unix.start && unix.input < unix.end) {
      unix.end = unix.input;
      const update = {
        start: this.getDate('start'),
        end: new Date(unix.end)
      }

      this.setDate('end', update.end);

      this.dateRangeFieldEvent.emit({
        name: this.name,
        value: update,
        errors: this.dateRangeField?.errors
      })

      datePicker.close();

      return;
    }
  }

  public handleDateChange(event: HTMLInputElement, controlName: string): void {
    const input = event.value;
    let customEvent = {
      name: this.name,
      value: {
        start: this.getDate('start'),
        end: this.getDate('end'),
      },
      errors: this.dateRangeField.get(controlName)?.errors? this.dateRangeField.get(controlName)?.errors: this.dateRangeField?.errors
    }

    if (!input?.length) {
      this.dateRangeFieldEvent.emit(customEvent)

      return;
    }

    const regex = this.isDateAllowed
      ? /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(\d{4})$/g
      : /^(0[1-9]|1[0-2])\/(\d{4})$/g;
    const isValidDate = input.match(regex);

    if (!isValidDate) {
      this.dateRangeFieldEvent.emit(customEvent)

      return;
    }

    const [mm, yyyy] = input.split('/');
    this.setDate(controlName, new Date(+yyyy, +mm - 1));
    if(this.isDateAllowed) {
      const [dd, mm, yyyy] = input.split('/');
      this.setDate(controlName, new Date(+yyyy, +mm - 1, +dd));
    }

    const errors = this.dateRangeField.get('start')?.errors
      ? this.dateRangeField.get('start')?.errors
      : this.dateRangeField.get('end')?.errors
      ? this.dateRangeField.get('end')?.errors
      : this.dateRangeField?.errors;
    
    customEvent = {
      name: this.name,
      value: {
        start: this.getDate('start'),
        end: this.getDate('end'),
      },
      errors
    }

    this.dateRangeFieldEvent.emit(customEvent)    
  }
  
  private getDate(controlName: string) {
    return this.dateRangeField.get(controlName)?.value;
  }

  private setDate(controlName: string, value: Date): void {
    this.dateRangeField.get(controlName)?.setValue(value)
  }
}